import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { CreditCard } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { toUserMessage } from "@/lib/errors";
import {
  useBillingProfile,
  useOpenCardPage,
  useRemoveCard,
  useUpdateBillingProfile,
} from "@/api/hooks/use-billing-profile";

const FINALIZE_TIMEOUT_MS = 60_000;

/** Billing-profile + payment-method setup card.
 *
 * Stripe redirects back with ?setup=complete (same URL for success AND
 * cancel), so completion is confirmed by polling the profile until the
 * webhook lands the payment method — not by trusting the query param. */
export function PaymentMethodCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [finalizing, setFinalizing] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState("");

  const profileQ = useBillingProfile({ pollForPaymentMethod: finalizing });
  const updateProfile = useUpdateBillingProfile();
  const openCardPage = useOpenCardPage();
  const removeCard = useRemoveCard();
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const [email, setEmail] = useState("");
  const [legalName, setLegalName] = useState("");
  const [editing, setEditing] = useState(false);

  // Returning from Stripe: clear the param, start the confirmation poll.
  useEffect(() => {
    if (searchParams.get("setup") === "complete") {
      searchParams.delete("setup");
      setSearchParams(searchParams, { replace: true });
      setFinalizing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!finalizing) return;
    const timer = setTimeout(() => {
      setFinalizing(false);
      setTimedOut(true);
    }, FINALIZE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [finalizing]);

  const profile = profileQ.data;
  useEffect(() => {
    if (finalizing && profile?.has_payment_method) {
      setFinalizing(false);
      setTimedOut(false);
    }
  }, [finalizing, profile?.has_payment_method]);

  if (!profile) return null;

  // Open the editor seeded with the values currently on file, so rotating an
  // address is an edit rather than a blank re-entry.
  function startEditing() {
    setEmail(profile?.billing_email ?? "");
    setLegalName(profile?.billing_legal_name ?? "");
    setError("");
    setEditing(true);
  }

  function cancelEditing() {
    setError("");
    setEditing(false);
  }

  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await updateProfile.mutateAsync({
        billing_email: email.trim(),
        billing_legal_name: legalName.trim(),
      });
      setEditing(false);
    } catch (err) {
      setError(toUserMessage(err));
    }
  }

  function handleOpenCardPage() {
    setError("");
    openCardPage.mutate(undefined, { onError: (err) => setError(toUserMessage(err)) });
  }

  function handleRemoveCard() {
    removeCard.mutate(undefined, {
      onError: (err) => setError(toUserMessage(err)),
      onSettled: () => setConfirmingRemove(false),
    });
  }

  const profileComplete = !!profile.billing_email && !!profile.billing_legal_name;
  // A Self-serve Customer's first card (ticket 20): Stripe's page collects the
  // billing email and legal name too, so there is no details form first.
  const oneStep = profile.self_serve && !profile.has_stripe_customer && !profile.has_payment_method;
  const showForm = isAdmin && ((!profileComplete && !oneStep) || editing);

  return (
    <div className="rounded-lg border border-line-subtle bg-surface p-5 space-y-4">
      <div className="flex items-center gap-2">
        <CreditCard size={16} className="text-fg-faint" />
        <h2 className="text-sm font-medium text-fg">Payment method</h2>
      </div>

      {error && (
        <div className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
          {error}
        </div>
      )}

      {/* ── Billing details: editable any time by an admin, even after a card
            is on file, so the invoice contact can be rotated. ── */}
      {showForm ? (
        <form onSubmit={handleProfileSubmit} className="space-y-3">
          <p className="text-sm text-fg-muted">
            {editing
              ? "Update your billing details. They'll appear on invoices and are synced to our payment processor."
              : "First, add your billing details. They'll appear on invoices."}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Billing email"
              className="rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40"
            />
            <input
              type="text"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="Company legal name"
              className="rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={!email.trim() || !legalName.trim() || updateProfile.isPending}
              className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:opacity-40"
            >
              {updateProfile.isPending ? "Saving…" : "Save billing details"}
            </button>
            {editing && (
              <button
                type="button"
                onClick={cancelEditing}
                disabled={updateProfile.isPending}
                className="rounded-md border border-line px-4 py-2 text-sm text-fg-muted transition-colors hover:text-fg disabled:opacity-40"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      ) : profileComplete ? (
        <div className="space-y-2">
          <p className="text-sm text-fg-muted">
            Billing as <span className="text-fg">{profile.billing_legal_name}</span>{" "}
            ({profile.billing_email}).
          </p>
          {isAdmin && (
            <button
              type="button"
              onClick={startEditing}
              className="text-sm font-medium text-electric-blue transition-colors hover:text-electric-blue/80"
            >
              Change billing details
            </button>
          )}
        </div>
      ) : oneStep && isAdmin ? null : (
        <p className="text-sm text-fg-muted">
          No billing details on file yet. A workspace admin can add them here.
        </p>
      )}

      {/* ── Payment method status / setup (hidden while editing details) ── */}
      {!editing &&
        (profile.has_payment_method ? (
          <div className="space-y-2">
            <p className="text-sm text-fg-muted">
              <span className="text-safe-green">✓</span> A payment method is on file.
              Invoices are charged automatically.
            </p>
            {isAdmin && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleOpenCardPage}
                    disabled={openCardPage.isPending}
                    className="rounded-md border border-line px-3 py-1.5 text-sm text-fg transition-colors hover:bg-canvas disabled:opacity-40"
                  >
                    {openCardPage.isPending ? "Opening Stripe…" : "Replace card"}
                  </button>
                  {profile.card_removable && (
                    <button
                      type="button"
                      onClick={() => setConfirmingRemove(true)}
                      className="rounded-md border border-line px-3 py-1.5 text-sm text-fg-muted transition-colors hover:text-fg"
                    >
                      Remove card
                    </button>
                  )}
                </div>
                {profile.card_removal_blocked_by && (
                  <p className="text-xs text-fg-faint">
                    The card can't be removed while {profile.card_removal_blocked_by}. You can replace it.
                  </p>
                )}
              </>
            )}
            {confirmingRemove && (
              <ConfirmDialog
                title="Remove your card?"
                message={
                  profile.self_serve
                    ? "With no card on file, Phantm refuses your requests once your Free week and Payment grace have ended (at once, if they already have) until a card is added. During Payment grace, requests go to your provider exactly as sent."
                    : "Invoices can't be charged automatically until a card is added again."
                }
                confirmLabel="Remove"
                isPending={removeCard.isPending}
                onConfirm={handleRemoveCard}
                onCancel={() => setConfirmingRemove(false)}
                variant="danger"
              />
            )}
          </div>
        ) : finalizing ? (
          <p className="text-sm text-fg-muted">Finalizing your payment method…</p>
        ) : (
          <>
            {timedOut && (
              <p className="text-sm text-caution-amber">
                We haven't received confirmation yet. If you completed the Stripe
                form, this can take a minute — refresh shortly. Otherwise, try
                again below.
              </p>
            )}

            {(profileComplete || oneStep) && isAdmin ? (
              <div className="space-y-2">
                <p className="text-sm text-fg-muted">
                  {oneStep
                    ? "Add a card on our payment processor's page, which also asks for your billing email and your company's legal name for invoices. Phantm never sees the card details."
                    : "Add a card via our payment processor — Phantm never sees the details."}
                </p>
                <button
                  type="button"
                  onClick={handleOpenCardPage}
                  disabled={openCardPage.isPending}
                  className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:opacity-40"
                >
                  {openCardPage.isPending ? "Opening Stripe…" : oneStep ? "Add card" : "Add payment method"}
                </button>
              </div>
            ) : !isAdmin && profileComplete ? (
              <p className="text-sm text-fg-muted">
                No payment method on file. A workspace admin can add one here.
              </p>
            ) : null}
          </>
        ))}
    </div>
  );
}
